import { effect, inject, Signal, untracked } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { AppError, SpinnerService, ToastType } from '@hglobal/fin-ui';
import { FormGroup } from '@ngneat/reactive-forms';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { switchMap } from 'rxjs';

import { EXCHANGE_API } from '@kr/components/exchange/api';
import { ExchangeEstimateReq, ExchangeEstimateRes, ExchangeReq, ExchangeRes } from '@kr/components/exchange/models';
import {
  setErrorStatusFor,
  setIdleStatusFor,
  setPendingStatusFor,
  setSuccessStatusFor,
  StoreHelperService,
  withRequestStatus,
} from '@kr/core';
import { WalletCard } from '@kr/models';

interface ExchangeState {
  walletOrgId: number;
  exchange: ExchangeRes;
  exchangeEstimate: ExchangeEstimateRes;
  wallets: Signal<WalletCard[]>;
  form: FormGroup<any>;
}

export const initialState: ExchangeState = {
  walletOrgId: null,
  exchange: null,
  exchangeEstimate: null,
  wallets: null,
  form: null,
};

export const ExchangeStore = signalStore(
  withState(initialState),
  withRequestStatus(['exchange', 'exchangeEstimate']),
  withProps(store => ({
    store,
    exchangeApi: inject(EXCHANGE_API),
    storeHelperService: inject(StoreHelperService),
    spinnerService: inject(SpinnerService),
  })),
  withMethods(({ store, exchangeApi }) => ({
    clearEstimate: () => {
      patchState(store, { exchangeEstimate: null });
    },
    saveWalletOrgId: (walletOrgId: number) => {
      patchState(store, { walletOrgId });
    },
    saveWallets: (wallets: Signal<WalletCard[]>) => {
      patchState(store, { wallets });
    },
    saveForm: (form: FormGroup<any>) => {
      patchState(store, { form });
    },
    clearExchange: () => {
      patchState(store, setIdleStatusFor('exchange'));
    },
    sendExchange: rxMethod<ExchangeReq>(
      switchMap(req => {
        patchState(store, setPendingStatusFor('exchange'));
        return exchangeApi.exchange(req, store.walletOrgId()).pipe(
          tapResponse({
            next: exchange => {
              patchState(store, { exchange }, setSuccessStatusFor('exchange'));
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('exchange', error)),
          }),
        );
      }),
    ),

    getExchangeEstimate: rxMethod<ExchangeEstimateReq>(
      switchMap(req => {
        patchState(store, setPendingStatusFor('exchangeEstimate'));
        return exchangeApi.exchangeEstimate(req, store.walletOrgId()).pipe(
          tapResponse({
            next: exchangeEstimate => {
              patchState(store, { exchangeEstimate }, setSuccessStatusFor('exchangeEstimate'));
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('exchangeEstimate', error)),
          }),
        );
      }),
    ),
  })),
  withHooks({
    onInit(store) {
      store.spinnerService.observe(toObservable(store.isLoading));

      effect(() => {
        if (store.error()) {
          untracked(() => store.storeHelperService.showToast(ToastType.Error, store.error()?.originalError?.error.error_description));
        }
      });
    },
  }),
);
